import styled, { css } from 'styled-components';
import { DeprecatedLinkButton as LinkButton, Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import {
  getMealPlanCancellationPolicyLabel,
  getOptionKey,
  promotionHasExtraInfo,
} from '@/app/components/hotel-detail/rooms/roomCommon';
import { getPromotionDisplayLabel } from '@/app/components/hotel-detail/rooms/promotionLabels';
import {
  hasMealPlanIncludedOptions,
  MEAL_PLAN_INCLUDED_OPTIONS_TITLE_KEY,
} from '@/app/components/hotel-detail/rooms/mealPlanIncludedOptions';
import { keyUpHandler } from '@/app/components/hotel-detail/common/common.helper';
import { groupPromotionsByLabel } from '@/app/components/hotel-detail/rooms/groupPromotionsByLabel';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { useRoomPanelOpenModal } from '@/app/components/hotel-detail/rooms/RoomPanelModalProvider';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import { useEvoModals } from '@/app/components/hotel-detail/evo/EvoModalsProvider';
import {
  BOARD_CLASS_ICON_INDEX,
  getMealPlanEvoIcon,
} from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanField';
import { Icon } from '@/app/common/icons/Icon';
import {
  SummaryHeader,
  SummaryRow,
  SummaryRows,
  SummarySection,
  IncludedItemClickable,
  SummaryRowText,
} from './summarySection.styles';
import { SummaryRowsSkeleton } from './SummaryRowsSkeleton';

const MEAL_PLAN_ICONS = getMealPlanEvoIcon();

const MealIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  // Centers the icon on the row's 24px first text line (see SummaryRow's img nudge).
  margin-top: 2px;
  color: ${({ theme }) => theme.kallisto.color.icon.static.iconPrimary};
`;

function CircleInfoImg({ alt = '', className }: { alt?: string; className?: string }) {
  return (
    <Icon
      category="Semantics"
      name="icon-circle-info"
      size={16}
      color="iconSecondary"
      alt={alt}
      className={className}
    />
  );
}

// Matches EvoRoomInfoRow: AddElementToLastWordInString flex-centres the icon beside the last word
// only for multi-word labels. A single-word label (e.g. "Non-refundable") renders as plain inline
// text where the baseline-aligned icon rides high, so nudge only that case down with position/top —
// which leaves the line box intact (margin/vertical-align would drag the whole label down).
const inlineInfoIconCss = (label: string) =>
  `margin-left: 4px;${label.split(' ').length === 1 ? ' position: relative; top: 2px;' : ''}`;

export function HotelSummarySection({
  onModifyClick,
  showModifyLink = true,
  showInfoIcons = true,
  headerVariant = 'Title5',
}: {
  onModifyClick?: () => void;
  showModifyLink?: boolean;
  showInfoIcons?: boolean;
  /**
   * MPH-842: the two surfaces size this header differently in Figma — Title5 (16/24) in the
   * desktop sidebar card, Title4 (18/28) in the mobile "Your trip" sheet, where it pairs with
   * the flight header (MobileTransportSummary). The tag stays an h5 either way.
   */
  headerVariant?: 'Title4' | 'Title5';
}) {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { openCancellationPoliciesModal, openPromotionInfoModal, openMealPlanInfoModal } = useEvoModals();
  const { selectedMealPlan, selectedRoom } = useSelectedMealPlan();
  const { offersData, isLoadingRooms } = useOffersContext();
  const { accommodationOnly } = useUrlSearchParams();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { openModal: openRoomDetailModal } = useRoomPanelOpenModal();

  const header = (
    <SummaryHeader>
      <Text variant={headerVariant} forceTag="h5">
        {hotelStaticData?.name ?? (
          // The skeleton is the heading's only child, so it — not the line box — sets the
          // header's height while the name loads. Reserve the variant's own line height so the
          // summary below does not jump down once useFetchHotelStatic resolves.
          <Placeholder height={headerVariant === 'Title4' ? '28px' : '24px'} width="140px" />
        )}
      </Text>
    </SummaryHeader>
  );

  // MPH-1144: until the selected rate is known, the summary is Figma's three icon-less skeleton
  // lines — which rows the rate shows (transfer, resort credit…) and their icons are only known
  // then. The change link waits for the rate too: there is no room to change yet.
  if (isLoadingRooms || !selectedRoom || !selectedMealPlan) {
    return (
      <SummarySection>
        {header}
        <SummaryRows>
          <SummaryRowsSkeleton firstLineWidth="56%" />
        </SummaryRows>
      </SummarySection>
    );
  }

  const transferData = offersData?.transfer;
  // MPH-1080: transfer inclusion reaches the page through two different responses — pre-attached by
  // us (the pricing proposal's structured transfer, which wins) and provided by the hotel (a
  // TRANSFER_INCLUDED promotion on the rate, which the result card announces and this row used to
  // drop). Grouped and labelled exactly like the resort credit below: result-page copy (MPH-721)
  // and the info modal listing the descriptions that say more than the label.
  const hotelTransfer = groupPromotionsByLabel(
    selectedMealPlan.promotions
      ?.filter((promotion) => promotion.type.toUpperCase() === 'TRANSFER_INCLUDED')
      .map((promotion) => ({ ...promotion, label: getPromotionDisplayLabel(promotion, t) }))
  )[0];
  const transferLabel = transferData ? t(transferData.outbound.key) : (hotelTransfer?.label ?? '');
  // The structured transfer carries a translation key and no descriptions, so only the hotel's own
  // promotion can have anything extra to show.
  const transferInfoDescriptions = transferData
    ? []
    : (hotelTransfer?.descriptions.filter((description) =>
        promotionHasExtraInfo({ label: hotelTransfer.label, description })
      ) ?? []);
  // Several RESORT_CREDIT entries can arrive with different descriptions (MPH-719) — group them so
  // the info modal lists every description, mirroring the room card (EvoRoomPromotions). The
  // displayed label is resolved up front (MPH-721) so distinct provider labels collapse into one
  // group and the row text, the info gate and the modal title all use the result-page copy.
  const resortCredit = groupPromotionsByLabel(
    selectedMealPlan.promotions
      ?.filter((promotion) => promotion.type === 'RESORT_CREDIT')
      .map((promotion) => ({ ...promotion, label: getPromotionDisplayLabel(promotion, t) }))
  )[0];
  const resortCreditInfoDescriptions =
    resortCredit?.descriptions.filter((description) =>
      promotionHasExtraInfo({ label: resortCredit.label, description })
    ) ?? [];

  const cancellationPolicyLabel = getMealPlanCancellationPolicyLabel(
    selectedMealPlan,
    accommodationOnly,
    t,
    localization.locale
  );

  const onOpenCancellationPolicies = () => openCancellationPoliciesModal(selectedRoom);

  const onOpenResortCreditInfo = () => {
    if (resortCredit) {
      openPromotionInfoModal({ ...resortCredit, descriptions: resortCreditInfoDescriptions });
    }
  };

  const onOpenTransferInfo = () => {
    if (hotelTransfer) {
      openPromotionInfoModal({ ...hotelTransfer, descriptions: transferInfoDescriptions });
    }
  };

  // MPH-1517: every row's leading icon is decorative (alt=""), so a row reads only its text; the
  // information icons after the text keep their names.
  const showTransferInfo = transferInfoDescriptions.length > 0 && showInfoIcons;
  const transferContent = (
    <SummaryRow
      css={`
        min-width: 0;
      `}
    >
      <Icon category="Transportation" name="icon-bus" size={20} color="iconPrimary" alt="" />
      <SummaryRowText>
        <AddElementToLastWordInString text={transferLabel}>
          {/* Decorative: the clickable row's accessible name already carries the transfer label. */}
          {showTransferInfo ? <CircleInfoImg css={inlineInfoIconCss(transferLabel)} /> : null}
        </AddElementToLastWordInString>
      </SummaryRowText>
    </SummaryRow>
  );

  // MPH-1067: the meal plan row carries the same included-options info icon as the room area — the
  // summary names the plan, the modal says which meals and drinks it actually covers. Suppressed
  // along with the other info icons inside the trip summary modal, where the row is plain text.
  const mealPlanLabel = t(getOptionKey(selectedMealPlan.boardClass, accommodationOnly));
  const showMealPlanInfo = showInfoIcons && hasMealPlanIncludedOptions(selectedMealPlan);
  const onOpenMealPlanInfo = () => openMealPlanInfoModal(selectedMealPlan.id);
  const mealPlanContent = (
    <SummaryRow
      css={`
        min-width: 0;
      `}
    >
      <MealIcon>{MEAL_PLAN_ICONS[BOARD_CLASS_ICON_INDEX[selectedMealPlan.boardClass]]}</MealIcon>
      <SummaryRowText>
        {showMealPlanInfo ? (
          // Bind the info icon to the last word so it never wraps onto its own line.
          <AddElementToLastWordInString text={mealPlanLabel}>
            <CircleInfoImg
              alt={t(MEAL_PLAN_INCLUDED_OPTIONS_TITLE_KEY) || 'Meal plan info'}
              css={inlineInfoIconCss(mealPlanLabel)}
            />
          </AddElementToLastWordInString>
        ) : (
          mealPlanLabel
        )}
      </SummaryRowText>
    </SummaryRow>
  );

  // Leading icon + label, shared between the plain and the clickable (info modal) variants below.
  // In the clickable variant the info icon rides inline after the label's last word.
  const showResortCreditInfo = resortCreditInfoDescriptions.length > 0 && showInfoIcons;
  const resortCreditContent = resortCredit ? (
    <SummaryRow
      css={`
        min-width: 0;
      `}
    >
      <Icon category="Financial" name="icon-cash" size={20} color="iconPrimary" alt="" />
      <SummaryRowText>
        <AddElementToLastWordInString text={resortCredit.label}>
          {showResortCreditInfo ? (
            <CircleInfoImg
              alt={t('vacation.select.evo_trip_overview.resort_credit_info') || 'Resort credit info'}
              css={inlineInfoIconCss(resortCredit.label)}
            />
          ) : null}
        </AddElementToLastWordInString>
      </SummaryRowText>
    </SummaryRow>
  ) : null;

  return (
    <SummarySection>
      {header}
      <SummaryRows>
        <SummaryRow
          css={`
            justify-content: space-between;
            min-width: 0;
          `}
        >
          <IncludedItemClickable
            onClick={() => openRoomDetailModal(selectedRoom)}
            onKeyUp={keyUpHandler(() => openRoomDetailModal(selectedRoom))}
            css={`
              min-width: 0;
            `}
          >
            <SummaryRow
              css={`
                min-width: 0;
              `}
            >
              <Icon category="Services" name="icon-door" size={20} color="iconPrimary" alt="" />
              <SummaryRowText>{selectedRoom.name}</SummaryRowText>
            </SummaryRow>
          </IncludedItemClickable>
          {showModifyLink && onModifyClick && (
            <LinkButton
              variant="accent"
              appearance="underlined"
              onClick={onModifyClick}
              css={css`
                ${({ theme }) => theme.kallisto.mixins.font('Body')};
                white-space: nowrap;
              `}
            >
              {t('vacation.select.cc.trip_overview.change_room')}
            </LinkButton>
          )}
        </SummaryRow>

        {showMealPlanInfo ? (
          <IncludedItemClickable
            onClick={onOpenMealPlanInfo}
            onKeyUp={keyUpHandler(onOpenMealPlanInfo)}
            css={`
              min-width: 0;
            `}
          >
            {mealPlanContent}
          </IncludedItemClickable>
        ) : (
          mealPlanContent
        )}

        {/* A plan whose label resolves empty (missing copy key) hides the row. */}
        {cancellationPolicyLabel ? (
          <IncludedItemClickable
            onClick={onOpenCancellationPolicies}
            onKeyUp={keyUpHandler(onOpenCancellationPolicies)}
            css={`
              min-width: 0;
            `}
          >
            <SummaryRow
              css={`
                min-width: 0;
              `}
            >
              <Icon
                category="Security and safety"
                name={selectedMealPlan.cancellationPolicy.type === 'REFUNDABLE' ? 'icon-shield' : 'icon-slashed-shield'}
                size={20}
                color="iconPrimary"
                alt=""
              />
              <SummaryRowText>
                {/* Bind the info icon to the last word so it never wraps onto its own line. */}
                <AddElementToLastWordInString text={cancellationPolicyLabel}>
                  {showInfoIcons ? (
                    <CircleInfoImg
                      alt={
                        t('vacation.select.evo_trip_overview.cancellation_policy_info') || 'Cancellation policy info'
                      }
                      css={inlineInfoIconCss(cancellationPolicyLabel)}
                    />
                  ) : null}
                </AddElementToLastWordInString>
              </SummaryRowText>
            </SummaryRow>
          </IncludedItemClickable>
        ) : null}

        {/* A transfer with no copy to show (no inclusion, or a missing copy key) hides the row
            instead of leaving a bare icon. */}
        {transferLabel ? (
          showTransferInfo ? (
            <IncludedItemClickable
              onClick={onOpenTransferInfo}
              onKeyUp={keyUpHandler(onOpenTransferInfo)}
              css={`
                min-width: 0;
              `}
            >
              {transferContent}
            </IncludedItemClickable>
          ) : (
            transferContent
          )
        ) : null}

        {resortCredit &&
          (resortCreditInfoDescriptions.length > 0 ? (
            <IncludedItemClickable
              onClick={onOpenResortCreditInfo}
              onKeyUp={keyUpHandler(onOpenResortCreditInfo)}
              css={`
                min-width: 0;
              `}
            >
              {resortCreditContent}
            </IncludedItemClickable>
          ) : (
            resortCreditContent
          ))}
      </SummaryRows>
    </SummarySection>
  );
}
